/**
 * AdminFloatingMenu — iter86.99. The floating glass menu for the admin
 * pages (One UI parity with the dashboard's DashboardMenu): cyber FAB
 * bottom-right on mobile (auto-hides on scroll down) / "Menu" pill in
 * the admin header on desktop. Absorbs the retired header clutter:
 * Setup Guide, invite-code copy, scope switch (old AdminMenu dropdown)
 * and the Back-to-Dashboard link.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Menu, HelpCircle, Copy, Check, Trophy, Settings, LayoutDashboard, MessageCircleWarning, Crown } from "lucide-react";
import GlassSheet from "@/components/onui/GlassSheet";
import ReportIssueModal from "@/components/ReportIssueModal";
import AdminFaqModal from "@/components/admin/AdminFaqModal";
import { hasSeenPro } from "@/pro/session";

const Row = ({ icon: Icon, label, sub, onClick, to, testId }) => {
    const cls = "w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:scale-[0.98] hover:bg-white/[0.06]";
    const body = (
        <>
            <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-white/[0.06]">
                {Icon && <Icon className="w-4 h-4" />}
            </span>
            <span className="flex-1 min-w-0">
                <span className="block font-display font-extrabold text-sm uppercase tracking-tight truncate">{label}</span>
                {sub && <span className="block text-[11px] text-zinc-400 truncate">{sub}</span>}
            </span>
        </>
    );
    return to ? (
        <Link to={to} data-testid={testId} onClick={onClick} className={cls}>{body}</Link>
    ) : (
        <button type="button" data-testid={testId} onClick={onClick} className={cls}>{body}</button>
    );
};

export default function AdminFloatingMenu({
    leagueId,
    league,
    tournament,
    scope,
    onSetScope,
}) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [fabHidden, setFabHidden] = useState(false);
    const [copied, setCopied] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    // iter87.471 — replaces the old auto-popup Setup Guide; opened
    // on-demand only, never automatically.
    const [faqOpen, setFaqOpen] = useState(false);
    // iter87.199.28 — "Return to Pro+" shows for both legacy and
    // Clerk-authed Pro+ users. Read the cross-mode marker at mount.
    const [hasProSession] = useState(() => hasSeenPro());

    useEffect(() => {
        let last = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setFabHidden(y > last && y > 80);
            last = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const copyInvite = () => {
        if (!league?.invite_code) return;
        try { navigator.clipboard?.writeText(league.invite_code)?.catch?.(() => {}); } catch { /* non-fatal */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };
    const swapScope = (next) => {
        setOpen(false);
        if (next === scope) return;
        onSetScope(next);
    };

    return (
        <>
            <button
                type="button"
                data-testid="admin-menu-trigger"
                onClick={() => setOpen(true)}
                className="hidden lg:inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-4 min-h-[40px] text-xs font-display font-extrabold uppercase tracking-wide transition-colors active:scale-95"
            >
                <Menu className="w-4 h-4" /> Menu
            </button>
            {createPortal(
                <button
                    type="button"
                    data-testid="admin-menu-fab"
                    onClick={() => setOpen(true)}
                    aria-label="Open admin menu"
                    className={`lg:hidden fixed bottom-5 right-4 z-40 w-14 h-14 rounded-full bg-cyber text-black border-2 border-black shadow-[0_10px_30px_rgba(255,204,0,0.35)] flex items-center justify-center transition-all duration-300 active:scale-90 ${fabHidden && !open ? "translate-y-24 opacity-0 pointer-events-none" : ""}`}
                >
                    <Menu className="w-6 h-6" strokeWidth={2.5} />
                </button>,
                document.body
            )}

            <GlassSheet open={open} onClose={() => setOpen(false)} title="Admin Menu" testId="admin-menu-sheet">
                <div className="space-y-0.5">
                    <Row
                        icon={LayoutDashboard}
                        label="Back to Dashboard"
                        sub={league?.name}
                        to={`/league/${leagueId}`}
                        onClick={() => setOpen(false)}
                        testId="admin-back-to-dashboard"
                    />
                    <Row
                        icon={HelpCircle}
                        label="How do I?"
                        sub="Help & FAQ - Search common questions"
                        onClick={() => { setOpen(false); setFaqOpen(true); }}
                        testId="open-admin-faq-btn"
                    />
                    {league?.invite_code && (
                        <Row
                            icon={copied ? Check : Copy}
                            label={<span className="font-mono tracking-[0.15em]">{copied ? "COPIED" : league.invite_code}</span>}
                            sub="Tap to copy the invite code"
                            onClick={copyInvite}
                            testId="admin-header-invite-code-pill"
                        />
                    )}
                    <div className="border-t border-white/10 my-2" />
                    <Row
                        icon={Trophy}
                        label="Tournament Admin"
                        sub={scope === "tournament" ? "You're here" : tournament?.name}
                        onClick={() => swapScope("tournament")}
                        testId="admin-menu-tournament-btn"
                    />
                    <Row
                        icon={Settings}
                        label="Rivalry Admin"
                        sub={scope === "league" ? "You're here" : "Members, invites, transfer"}
                        onClick={() => swapScope("league")}
                        testId="admin-menu-league-btn"
                    />
                    {/* iter87.105.1 — Report an Issue promoted to the admin
                        floating menu so admins can file from any tab. */}
                    <Row
                        icon={MessageCircleWarning}
                        label="Report an issue"
                        sub="Bug, confusion, or feature idea"
                        onClick={() => { setOpen(false); setReportOpen(true); }}
                        testId="admin-menu-report-issue-btn"
                    />
                    {hasProSession ? (
                        <>
                            <div className="border-t border-white/10 my-2" />
                            <Row
                                icon={Crown}
                                label="Return to Pro+"
                                sub="Back to your Pro+ console"
                                to="/rivalry-pro"
                                onClick={() => setOpen(false)}
                                testId="return-to-pro-plus-btn"
                            />
                        </>
                    ) : (
                        // iter87.199.159 — Free admins get the self-serve
                        // upgrade path here too, mirroring the Pro+
                        // "Return to Pro+" row above (mutually exclusive).
                        <>
                            <div className="border-t border-white/10 my-2" />
                            <Row
                                icon={Crown}
                                label="Sign up for Pro+"
                                sub="Unlimited tournaments, AI recaps & more"
                                to="/rivalry-pro"
                                onClick={() => setOpen(false)}
                                testId="signup-for-pro-plus-btn"
                            />
                        </>
                    )}
                </div>
            </GlassSheet>
            <ReportIssueModal
                open={reportOpen}
                onClose={() => setReportOpen(false)}
                leagueId={leagueId}
            />
            <AdminFaqModal open={faqOpen} onClose={() => setFaqOpen(false)} />
        </>
    );
}
